import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import '../infrastructure.css';

const products = [
  { to: '/infrastructure/payroll', label: 'Payroll & Benefits', desc: 'Salaries, benefits, tax filing' },
  { to: '/infrastructure/wallets', label: 'Virtual Wallets', desc: 'Accounts, transfers, ledger' },
  { to: '/infrastructure/collect', label: 'Collect', desc: 'Recurring payments, deductions' },
  { to: '/infrastructure/connect', label: 'Connect', desc: 'APIs, webhooks, SDKs' },
  { to: '/infrastructure/comply', label: 'Comply', desc: 'KYC, AML, audit trails' },
  { to: '/infrastructure/agents', label: 'Agent Wallets', desc: 'AI agents with spending rules' },
];

export default function InfraNav({ transparent }) {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const ref = useRef(null);
  const navClass = transparent ? 'infra-nav2' : 'infra-nav2 infra-nav2-solid';

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  useEffect(() => {
    if (mobileOpen) document.body.style.overflow = 'hidden';
    else document.body.style.overflow = '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileOpen]);

  return (
    <nav className={navClass} data-testid="infra-nav">
      <div className="infra-nav2-inner">
        <Link to="/" className="infra-nav2-logo" data-testid="nav-logo">
          <img src="/pebbles-logo.svg" alt="Pebbles" />
        </Link>

        <div className="infra-nav2-center">
          <div className="infra-nav2-dropdown" ref={ref}>
            <button
              className={`infra-nav2-link ${open ? 'infra-nav2-link-open' : ''}`}
              onClick={() => setOpen(o => !o)}
              data-testid="nav-products-dropdown"
            >
              Products
              <svg width="10" height="6" viewBox="0 0 10 6" fill="none" style={{ marginLeft: 4, transition: 'transform 0.2s', transform: open ? 'rotate(180deg)' : 'rotate(0)' }}>
                <path d="M1 1l4 4 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </button>
            {open && (
              <div className="infra-mega-menu" data-testid="nav-products-menu">
                <div className="infra-mega-inner">
                  <div className="infra-mega-grid">
                    {products.map(p => (
                      <Link key={p.to} to={p.to} className="infra-mega-item" onClick={() => setOpen(false)}>
                        <span className="infra-mega-label">{p.label}</span>
                        <span className="infra-mega-desc">{p.desc}</span>
                      </Link>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
          <Link
            to="/infrastructure/developers"
            className={`infra-nav2-link ${pathname === '/infrastructure/developers' ? 'infra-nav2-link-active' : ''}`}
            data-testid="nav-developers"
          >
            Developers
          </Link>
        </div>

        <div className="infra-nav2-right">
          <Link to="/login" className="infra-nav2-login" data-testid="nav-signin">Log in</Link>
          <Link to="/wallet-login" className="infra-nav2-outline" data-testid="nav-start-building">Start building</Link>
          <a href="mailto:hello@thcohq.com" className="infra-nav2-solid-btn" data-testid="nav-contact-sales">Talk to us</a>
        </div>

        {/* Mobile hamburger */}
        <button
          className="infra-nav2-hamburger"
          onClick={() => setMobileOpen(o => !o)}
          data-testid="nav-mobile-toggle"
          aria-label="Menu"
        >
          {mobileOpen ? (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
          ) : (
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
          )}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="infra-mobile-menu" data-testid="nav-mobile-menu">
          <div className="infra-mobile-section">
            <div className="infra-mobile-heading">Products</div>
            {products.map(p => (
              <Link key={p.to} to={p.to} className="infra-mobile-link" onClick={() => setMobileOpen(false)}>
                <span className="infra-mobile-label">{p.label}</span>
                <span className="infra-mobile-desc">{p.desc}</span>
              </Link>
            ))}
          </div>
          <Link to="/infrastructure/developers" className="infra-mobile-link infra-mobile-link-top" onClick={() => setMobileOpen(false)}>
            <span className="infra-mobile-label">Developers</span>
            <span className="infra-mobile-desc">APIs, SDKs, and documentation</span>
          </Link>
          <div className="infra-mobile-ctas">
            <Link to="/login" className="infra-mobile-cta-outline" onClick={() => setMobileOpen(false)}>Log in</Link>
            <a href="mailto:hello@thcohq.com" className="infra-mobile-cta-solid" onClick={() => setMobileOpen(false)}>Talk to us</a>
          </div>
        </div>
      )}
    </nav>
  );
}
